import React, { useState, useEffect, useMemo } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase/config';

interface SocialMediaAuditEntry {
  id: string;
  reportId: string;
  adminUserId: string;
  action: string;
  timestamp: any;
  shareContent?: {
    text: string;
    imageCount: number;
    hashtagCount: number;
    hasLocation: boolean;
  };
  platformResults?: {
    [platform: string]: {
      success: boolean;
      postId?: string;
      error?: string;
      processingTimeMs?: number;
    };
  };
  selectedPlatforms?: string[];
  successfulPosts?: number;
  totalAttempted?: number;
}

interface SocialMediaAuditLogProps {
  reportId?: string;
  className?: string;
}

export const SocialMediaAuditLog: React.FC<SocialMediaAuditLogProps> = ({
  reportId,
  className = ''
}) => {
  const [auditEntries, setAuditEntries] = useState<SocialMediaAuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Memoize Firebase Function
  const getSocialMediaAuditLogFunction = useMemo(() => 
    httpsCallable(functions, 'getSocialMediaAuditLog'), []
  );

  useEffect(() => {
    loadAuditLog();
  }, [reportId]);

  const loadAuditLog = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await getSocialMediaAuditLogFunction({ 
        reportId,
        limit: reportId ? 50 : 20 // More entries for specific report, fewer for general view
      });
      
      const data = result.data as { success: boolean; entries?: SocialMediaAuditEntry[]; error?: string };
      
      if (data.success && data.entries) {
        setAuditEntries(data.entries);
      } else {
        throw new Error(data.error || 'Failed to load audit log');
      }
    } catch (err) {
      console.error('Error loading social media audit log:', err);
      setError(err instanceof Error ? err.message : 'Failed to load audit log');
      setAuditEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp: any): string => {
    if (!timestamp) return 'Unknown';
    
    let date: Date;
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      date = timestamp.toDate();
    } else if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else {
      date = new Date(timestamp);
    }
    
    return date.toLocaleString();
  };

  const getPlatformIcon = (platform: string): string => {
    switch (platform.toLowerCase()) {
      case 'facebook': return '📘';
      case 'twitter': return '🐦';
      case 'instagram': return '📷';
      default: return '📱';
    }
  };

  if (loading) {
    return (
      <div className={`space-y-3 ${className}`}>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Social Media Sharing History</h3>
        </div>
        <div className="animate-pulse space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-gray-100 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="w-32 h-4 bg-gray-200 rounded"></div>
                <div className="w-24 h-4 bg-gray-200 rounded"></div>
              </div>
              <div className="w-full h-3 bg-gray-200 rounded mb-2"></div>
              <div className="flex space-x-2">
                <div className="w-16 h-6 bg-gray-200 rounded"></div>
                <div className="w-16 h-6 bg-gray-200 rounded"></div>
                <div className="w-16 h-6 bg-gray-200 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`space-y-3 ${className}`}>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Social Media Sharing History</h3>
          <button
            onClick={loadAuditLog}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            Retry
          </button>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (auditEntries.length === 0) {
    return (
      <div className={`space-y-3 ${className}`}>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Social Media Sharing History</h3>
          <button
            onClick={loadAuditLog}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            Refresh
          </button>
        </div>
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <div className="text-gray-400 text-2xl mb-2">📱</div>
          <p className="text-gray-500">No social media sharing history found</p>
          {reportId && (
            <p className="text-gray-400 text-sm mt-1">This report hasn't been shared on social media yet</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">
          Social Media Sharing History
          {reportId && ` (${auditEntries.length})`}
        </h3>
        <button
          onClick={loadAuditLog}
          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
        >
          Refresh
        </button>
      </div>

      <div className="space-y-3 max-h-96 overflow-y-auto">
        {auditEntries.map((entry) => (
          <div key={entry.id} className="bg-white border border-gray-200 rounded-lg p-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-900">
                  {entry.action === 'automatic_social_sharing' ? 'Automatic Sharing' : entry.action}
                </span>
                {!reportId && (
                  <span className="text-xs text-gray-500">
                    Report: {entry.reportId.substring(0, 8)}...
                  </span>
                )}
              </div>
              <span className="text-xs text-gray-500">
                {formatTimestamp(entry.timestamp)}
              </span>
            </div>

            {/* Platform Results */}
            {entry.platformResults && (
              <div className="mb-3">
                <div className="flex flex-wrap gap-2">
                  {Object.entries(entry.platformResults).map(([platform, result]) => (
                    <div
                      key={platform}
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        result.success
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      <span className="mr-1">{getPlatformIcon(platform)}</span>
                      <span className="capitalize">{platform}</span>
                      {result.success ? (
                        <span className="ml-1">✓</span>
                      ) : (
                        <span className="ml-1">✗</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Summary Stats */}
            {entry.successfulPosts !== undefined && entry.totalAttempted !== undefined && (
              <div className="flex items-center justify-between text-xs text-gray-600">
                <span>
                  Success Rate: {entry.successfulPosts}/{entry.totalAttempted} platforms
                </span>
                {entry.shareContent && (
                  <span>
                    {entry.shareContent.imageCount} image{entry.shareContent.imageCount !== 1 ? 's' : ''}, {entry.shareContent.hashtagCount} hashtag{entry.shareContent.hashtagCount !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            )}

            {/* Error Details */}
            {entry.platformResults && Object.values(entry.platformResults).some(r => !r.success) && (
              <div className="mt-2 pt-2 border-t border-gray-100">
                <details className="text-xs">
                  <summary className="cursor-pointer text-gray-600 hover:text-gray-800">
                    View Error Details
                  </summary>
                  <div className="mt-2 space-y-1">
                    {Object.entries(entry.platformResults)
                      .filter(([_, result]) => !result.success)
                      .map(([platform, result]) => (
                        <div key={platform} className="text-red-600">
                          <span className="font-medium capitalize">{platform}:</span> {result.error}
                        </div>
                      ))}
                  </div>
                </details>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};